import { useEffect } from 'react';

/**
 * usePageMeta — auto-generating SEO engine.
 *
 * Given a title (and optionally description/canonicalUrl/keywords), it:
 *   - Clamps the <title> to ≤60 chars and normalizes the meta description
 *     to a search-friendly length (≤160 chars).
 *   - Auto-derives a keyword list from the title + description.
 *   - Injects robots, canonical, Open Graph, Twitter Card, geo tags and
 *     JSON-LD (Organization / WebSite / BreadcrumbList) structured data.
 *
 * Only `title` is required; everything else falls back to sensible defaults.
 */

const SITE_NAME = 'CashMyProperty.ai';
const BASE_URL = 'https://cashmyproperty.ai';
const DEFAULT_OG_IMAGE =
  'https://readdy.ai/api/search-image?query=Premium%20real%20estate%20land%20monetization%20brand%20hero%2C%20dark%20navy%20and%20gold%20cinematic%20landscape%2C%20abstract%20land%20plot%20with%20financial%20growth%20overlay&width=1200&height=630&seq=og-default-01&orientation=landscape';

const STOPWORDS = new Set([
  'a','an','the','and','or','but','for','with','from','your','you','are','our',
  'of','to','in','on','at','by','is','it','its','we','us','be','this','that',
  'get','can','into','without','not','no','more','most','new','every','full',
  'all','land','property','property.ai','cashmyproperty.ai',
]);

interface UsePageMetaOptions {
  title: string;
  description?: string;
  canonicalUrl?: string;
  type?: 'website' | 'article' | 'product';
  keywords?: string;
  ogImage?: string;
}

function clampTitle(title: string): string {
  const t = title.trim().replace(/\s+/g, ' ');
  if (t.length <= 60) return t;
  return t.slice(0, 57).replace(/[,;&\s]+$/g, '').trimEnd() + '...';
}

function normalizeDescription(desc: string): string {
  const d = desc.trim().replace(/\s+/g, ' ');
  if (d.length <= 160) return d;
  let cut = d.slice(0, 157);
  const lastSpace = cut.lastIndexOf(' ');
  if (lastSpace > 110) cut = cut.slice(0, lastSpace);
  return cut.replace(/[,;&\s]+$/g, '').trimEnd() + '...';
}

function deriveKeywords(title: string, description: string): string {
  const words = (title + ' ' + description)
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, ' ')
    .split(/\s+/)
    .filter((w) => w.length > 2 && !STOPWORDS.has(w));
  const seen = new Set<string>();
  const out: string[] = [];
  for (const w of words) {
    const clean = w.replace(/-/g, ' ').trim();
    if (!clean) continue;
    if (!seen.has(clean)) {
      seen.add(clean);
      out.push(clean);
    }
    if (out.length >= 8) break;
  }
  return out.join(', ');
}

function upsertMeta(selector: string, attribute: 'name' | 'property', key: string, content: string): void {
  let el = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attribute, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

export function usePageMeta({
  title,
  description,
  canonicalUrl,
  type = 'website',
  keywords,
  ogImage = DEFAULT_OG_IMAGE,
}: UsePageMetaOptions) {
  useEffect(() => {
    const finalTitle = clampTitle(title);
    const finalDesc = normalizeDescription(description || title);
    const finalKeywords = keywords || deriveKeywords(title, description || '');
    const path = window.location.pathname.replace(/\/+$/, '') || '/';
    const url = canonicalUrl || `${BASE_URL}${path}`;

    document.title = finalTitle;

    // Basic SEO tags
    upsertMeta('meta[name="description"]', 'name', 'description', finalDesc);
    upsertMeta('meta[name="keywords"]', 'name', 'keywords', finalKeywords);
    upsertMeta('meta[name="robots"]', 'name', 'robots', 'index, follow');
    upsertMeta('meta[name="author"]', 'name', 'author', SITE_NAME);
    upsertMeta('meta[name="last-modified"]', 'name', 'last-modified', new Date().toISOString().slice(0, 10));

    // Canonical
    let canonical = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = url;

    // Open Graph
    upsertMeta('meta[property="og:type"]', 'property', 'og:type', type);
    upsertMeta('meta[property="og:site_name"]', 'property', 'og:site_name', SITE_NAME);
    upsertMeta('meta[property="og:title"]', 'property', 'og:title', finalTitle);
    upsertMeta('meta[property="og:description"]', 'property', 'og:description', finalDesc);
    upsertMeta('meta[property="og:url"]', 'property', 'og:url', url);
    upsertMeta('meta[property="og:image"]', 'property', 'og:image', ogImage);
    upsertMeta('meta[property="og:locale"]', 'property', 'og:locale', 'en_IN');

    // Twitter Card
    upsertMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
    upsertMeta('meta[name="twitter:title"]', 'name', 'twitter:title', finalTitle);
    upsertMeta('meta[name="twitter:description"]', 'name', 'twitter:description', finalDesc);
    upsertMeta('meta[name="twitter:image"]', 'name', 'twitter:image', ogImage);

    // Local / geo tags (Karnataka HQ)
    upsertMeta('meta[name="geo.region"]', 'name', 'geo.region', 'IN-KA');
    upsertMeta('meta[name="geo.placename"]', 'name', 'geo.placename', 'Bengaluru, Karnataka');
    upsertMeta('meta[name="geo.position"]', 'name', 'geo.position', '12.9716;77.5946');
    upsertMeta('meta[name="ICBM"]', 'name', 'ICBM', '12.9716, 77.5946');

    // Structured data — Organization, WebSite (with SearchAction) + Breadcrumb
    const orgJson = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: SITE_NAME,
      url: BASE_URL,
      telephone: '+919035063455',
      email: 'support@cashmyproperty.ai',
      areaServed: 'IN',
    };
    const websiteJson = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: SITE_NAME,
      url: BASE_URL,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${BASE_URL}/listings?search={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    };
    const breadcrumbJson = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
        { '@type': 'ListItem', position: 2, name: finalTitle.slice(0, 40), item: url },
      ],
    };

    document.querySelectorAll('script[data-seo-jsonld]').forEach((n) => n.remove());
    for (const schema of [orgJson, websiteJson, breadcrumbJson]) {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute('data-seo-jsonld', 'true');
      s.textContent = JSON.stringify(schema);
      document.head.appendChild(s);
    }
  }, [title, description, canonicalUrl, type, keywords, ogImage]);
}